import { ButtonType, EpicIcons } from "@constellation-academy/epic-ui";
import { useFormik } from "formik";
import { Divider } from "primereact/divider";
import { useCallback, useState, type FC } from "react";
import { useMutation } from "react-relay";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { Button, ButtonVariant } from "@components/button";
import { DefaultTextFieldComponent } from "@components/DefaultTextInput";
import { InputText } from "@components/input-text";
import { ValidatedField } from "@components/ValidatedField";
import { SelectDiscountCodeField } from "@features/discount-codes/select-discount-code-field";
import { DiscountCode } from "@features/discount-codes/select-discount-code-field/select-discount-code-field.interface";
import { SelectProductField } from "@features/products/select-product-field";
import { Product } from "@features/products/select-product-field-dialog/select-product-field-dialog.interface";
import { useTranslation } from "@i18n/hooks";
import { upsertManualOrderModal_CalculateManualOrderPriceMutation } from "@relay/upsertManualOrderModal_CalculateManualOrderPriceMutation.graphql";
import { upsertManualOrderModal_UpsertManualOrderModalMutation } from "@relay/upsertManualOrderModal_UpsertManualOrderModalMutation.graphql";
import { H2Span, H3Span, P2Span } from "@themes/font-tags";
import {
	CALCULATE_MANUAL_ORDER_PRICE,
	CREATE_MANUAL_ORDER_MODAL,
} from "./upsert-manual-order-modal.graphql";
import { manualOrderSchema } from "./upsert-manual-order-modal.schema";
import {
	selectedItemClass,
	selectedItemContentClass,
	selectedItemFieldsClass,
	selectedItemsContainerClass,
	selectedItemsListClass,
	selectProductOrDiscountCodeContainerClass,
	selectProductOrDiscountCodeHeaderClass,
	totalPriceContainerClass,
} from "./upsert-manual-order-modal.styles";
import {
	UpsertManualOrderModalFormState,
	UpsertManualOrderModalProps,
	UpsertManualOrderModalTotalPrice,
} from "./upsert-manual-order-modal.types";

export const UpsertManualOrderModal: FC<UpsertManualOrderModalProps> = ({
	accountId,
	connections,
	onCreated,
}) => {
	const { t } = useTranslation("common");
	const [selectedProducts, setSelectedProducts] = useState<
		UpsertManualOrderModalFormState["productSelection"]
	>([]);
	const [selectedDiscountCodes, setSelectedDiscountCodes] = useState<DiscountCode[]>([]);

	const [tempSelectedProduct, setTempSelectedProduct] = useState<Product | undefined>(undefined);
	const [tempSelectedDiscountCode, setTempSelectedDiscountCode] = useState<
		DiscountCode | undefined
	>(undefined);

	const [isSelectProductOpen, setIsSelectProductOpen] = useState(false);
	const [isSelectDiscountCodeOpen, setIsSelectDiscountCodeOpen] = useState(false);

	const [totalPrice, setTotalPrice] = useState<UpsertManualOrderModalTotalPrice | undefined>({
		grossPrice: 0,
		netPrice: 0,
		taxRatePercentage: 0,
	});

	const [createManualOrder] =
		useMutation<upsertManualOrderModal_UpsertManualOrderModalMutation>(
			CREATE_MANUAL_ORDER_MODAL,
		);

	const [calculateManualOrderPrice] =
		useMutation<upsertManualOrderModal_CalculateManualOrderPriceMutation>(
			CALCULATE_MANUAL_ORDER_PRICE,
		);

	const form = useFormik<UpsertManualOrderModalFormState>({
		initialValues: {
			invoiceNumber: "",
			productSelection: [],
			discountSelection: [],
			forAccountId: accountId,
		},
		validationSchema: toFormikValidationSchema(manualOrderSchema),
		onSubmit: (values) => {
			const valuesToSubmit = {
				...values,
				productSelection: selectedProducts.map((product) => ({
					productId: product.productId,
					amount: product.amount,
					price: {
						netPrice: product.price.netPrice,
						grossPrice: product.price.grossPrice,
						taxRatePercentage: product.price.taxRatePercentage,
					},
				})),
				discountSelection: selectedDiscountCodes
					.filter((discountCode) => discountCode.id)
					.map((discountCode) => discountCode.id!),
			};

			createManualOrder({
				variables: {
					input: valuesToSubmit,
					connections: connections,
				},
				onCompleted: () => {
					onCreated?.(valuesToSubmit);
				},
			});
		},
	});

	const handleProductsOrDiscountCodesChange = useCallback(
		(
			newSelectedProducts: UpsertManualOrderModalFormState["productSelection"],
			newSelectedDiscountCodes: DiscountCode[],
		) => {
			calculateManualOrderPrice({
				variables: {
					input: {
						productSelection: newSelectedProducts.map((product) => ({
							productId: product.productId,
							amount: product.amount,
							price: {
								netPrice: product.price.netPrice,
								grossPrice: product.price.grossPrice,
								taxRatePercentage: product.price.taxRatePercentage,
							},
						})),
						discountSelection: newSelectedDiscountCodes.map(
							(discountCode) => discountCode.id!,
						),
					},
				},
				onCompleted(response, errors) {
					if (errors) {
						return;
					}
					setTotalPrice(
						response.Admin.Billing.calculatePriceForManualOrder?.price ?? undefined,
					);
				},
			});
		},
		[calculateManualOrderPrice],
	);

	const handleFlushTempProductToProducts = useCallback(() => {
		if (!tempSelectedProduct) return;
		const newSelectedProducts = [
			...selectedProducts,
			{
				productId: tempSelectedProduct.id!,
				title: tempSelectedProduct.title,
				amount: 1,
				price: { netPrice: 0, grossPrice: 0, taxRatePercentage: 0 },
			},
		];
		setSelectedProducts(newSelectedProducts);
		setTempSelectedProduct(undefined);
		setIsSelectProductOpen(false);
		handleProductsOrDiscountCodesChange(newSelectedProducts, selectedDiscountCodes);
	}, [
		tempSelectedProduct,
		selectedProducts,
		selectedDiscountCodes,
		handleProductsOrDiscountCodesChange,
	]);

	const handleFlushTempDiscountCodeToDiscountCodes = useCallback(() => {
		if (!tempSelectedDiscountCode) return;
		const newSelectedDiscountCodes = [...selectedDiscountCodes, tempSelectedDiscountCode];
		setSelectedDiscountCodes(newSelectedDiscountCodes);
		setTempSelectedDiscountCode(undefined);
		setIsSelectDiscountCodeOpen(false);
		handleProductsOrDiscountCodesChange(selectedProducts, newSelectedDiscountCodes);
	}, [
		tempSelectedDiscountCode,
		selectedDiscountCodes,
		handleProductsOrDiscountCodesChange,
		selectedProducts,
	]);

	const handleRemoveProduct = useCallback(
		(productId?: string) => {
			if (!productId) return;
			const newSelectedProducts = selectedProducts.filter((p) => p.productId !== productId);
			setSelectedProducts(newSelectedProducts);
			handleProductsOrDiscountCodesChange(newSelectedProducts, selectedDiscountCodes);
		},
		[handleProductsOrDiscountCodesChange, selectedProducts, selectedDiscountCodes],
	);

	const handleRemoveDiscountCode = useCallback(
		(discountCodeId?: string) => {
			if (!discountCodeId) return;
			const newSelectedDiscountCodes = selectedDiscountCodes.filter(
				(d) => d.id !== discountCodeId,
			);
			setSelectedDiscountCodes(newSelectedDiscountCodes);
			handleProductsOrDiscountCodesChange(selectedProducts, newSelectedDiscountCodes);
		},
		[handleProductsOrDiscountCodesChange, selectedProducts, selectedDiscountCodes],
	);

	const createProductAmountFieldChangeHandler = useCallback(
		(productId: string) => {
			return (text?: string) => {
				let newValue = Number.parseInt(text ?? "0", 10);
				if (isNaN(newValue)) newValue = 0;
				const newSelectedProducts = selectedProducts.map((p) =>
					p.productId === productId ? { ...p, amount: newValue } : p,
				);
				setSelectedProducts(newSelectedProducts);

				handleProductsOrDiscountCodesChange(newSelectedProducts, selectedDiscountCodes);
			};
		},
		[selectedProducts, handleProductsOrDiscountCodesChange, selectedDiscountCodes],
	);

	const createProductPriceFieldChangeHandler = useCallback(
		(productId: string, priceType: "netPrice" | "grossPrice" | "taxRatePercentage") => {
			return (text?: string) => {
				let newValue = Number.parseFloat(text ?? "0");
				if (isNaN(newValue)) newValue = 0;

				const newSelectedProducts = selectedProducts.map((p) =>
					p.productId === productId
						? { ...p, price: { ...p.price, [priceType]: newValue } }
						: p,
				);

				setSelectedProducts(newSelectedProducts);
				handleProductsOrDiscountCodesChange(newSelectedProducts, selectedDiscountCodes);
			};
		},
		[selectedProducts, handleProductsOrDiscountCodesChange, selectedDiscountCodes],
	);

	return (
		<form onSubmit={form.handleSubmit}>
			<ValidatedField<UpsertManualOrderModalFormState, string>
				name="invoiceNumber"
				label={t("order.invoiceNumber")}
				component={DefaultTextFieldComponent}
				formikConfig={form}
			/>
			<div className={selectedItemsContainerClass}>
				<H3Span>{t("order.products")}</H3Span>
				<div className={selectedItemsListClass}>
					{selectedProducts.map((product) => (
						<div key={product.productId} className={selectedItemClass}>
							<div className={selectedItemContentClass}>
								<P2Span>{product.title!}</P2Span>
								<div className={selectedItemFieldsClass}>
									<InputText
										label={t("order.amount")}
										value={product.amount.toString()}
										onChange={createProductAmountFieldChangeHandler(
											product.productId,
										)}
									/>
									<InputText
										label={t("order.netPrice")}
										value={product.price.netPrice.toString()}
										onChange={createProductPriceFieldChangeHandler(
											product.productId,
											"netPrice",
										)}
									/>
									<InputText
										label={t("order.taxRate")}
										value={product.price.taxRatePercentage.toString()}
										onChange={createProductPriceFieldChangeHandler(
											product.productId,
											"taxRatePercentage",
										)}
									/>
								</div>
							</div>
							<Button
								type={ButtonType.Button}
								icon={EpicIcons.TRASH}
								onClick={() => handleRemoveProduct(product.productId)}
							/>
						</div>
					))}
					{selectedProducts.length === 0 && <div>{t("order.noProductsSelected")}</div>}
				</div>
				<Button
					type={ButtonType.Button}
					label={t("order.addAnotherProduct")}
					onClick={() => setIsSelectProductOpen(true)}
				/>
			</div>
			{isSelectProductOpen && (
				<div className={selectProductOrDiscountCodeContainerClass}>
					<div className={selectProductOrDiscountCodeHeaderClass}>
						<H2Span>{t("order.addProduct")}</H2Span>
						<Button
							type={ButtonType.Button}
							variant={ButtonVariant.Textlink}
							icon={EpicIcons.TIMES}
							onClick={() => setIsSelectProductOpen(false)}
						/>
					</div>
					<SelectProductField
						fieldName="productSelection"
						required={true}
						isValid={true}
						disabled={false}
						fieldValue={tempSelectedProduct}
						updateField={(value) => {
							if (!value) {
								setTempSelectedProduct(undefined);
								return;
							}
							setTempSelectedProduct(value);
						}}
					/>
					<Button
						type={ButtonType.Button}
						variant={ButtonVariant.Strong}
						label={t("order.addProduct")}
						onClick={handleFlushTempProductToProducts}
					/>
				</div>
			)}
			<div className={selectedItemsContainerClass}>
				<H3Span>{t("order.discountCodes")}</H3Span>
				<div className={selectedItemsListClass}>
					{selectedDiscountCodes.map((discountCode) => (
						<div key={discountCode.code} className={selectedItemClass}>
							<P2Span>{discountCode.code}</P2Span>
							<Button
								icon={EpicIcons.TRASH}
								onClick={() => handleRemoveDiscountCode(discountCode.id)}
							/>
						</div>
					))}
					{selectedDiscountCodes.length === 0 && (
						<div>{t("order.noDiscountCodesSelected")}</div>
					)}
				</div>
				<Button
					type={ButtonType.Button}
					label={t("order.addDiscountCode")}
					onClick={() => setIsSelectDiscountCodeOpen(true)}
				/>
			</div>
			{isSelectDiscountCodeOpen && (
				<div className={selectProductOrDiscountCodeContainerClass}>
					<div className={selectProductOrDiscountCodeHeaderClass}>
						<H2Span>{t("order.addDiscountCode")}</H2Span>
						<Button
							type={ButtonType.Button}
							variant={ButtonVariant.Textlink}
							icon={EpicIcons.TIMES}
							onClick={() => setIsSelectDiscountCodeOpen(false)}
						/>
					</div>
					<SelectDiscountCodeField
						fieldName="discountSelection"
						required={true}
						isValid={true}
						disabled={false}
						fieldValue={tempSelectedDiscountCode}
						updateField={(value) => {
							if (!value) {
								setTempSelectedDiscountCode(undefined);
								return;
							}
							setTempSelectedDiscountCode(value);
						}}
					/>
					<Button
						type={ButtonType.Button}
						label={t("order.saveDiscountCode")}
						onClick={handleFlushTempDiscountCodeToDiscountCodes}
					/>
				</div>
			)}
			<Divider />
			<div className={totalPriceContainerClass}>
				<H3Span>{t("order.totalPrice")}</H3Span>
				<P2Span>
					{t("order.grossPrice")}: {totalPrice?.grossPrice}€
				</P2Span>
				<P2Span>
					{t("order.netPrice")}: {totalPrice?.netPrice}€
				</P2Span>
				<P2Span>
					{t("order.taxRate")}: {totalPrice?.taxRatePercentage}%
				</P2Span>
			</div>

			<Divider />
			<Button
				type={ButtonType.Submit}
				variant={ButtonVariant.Strong}
				label={t("order.createOrder")}
				stretch
			/>
		</form>
	);
};
